import { useScroll, useTransform } from 'framer-motion'
import capitalize from 'lodash/capitalize'
import Head from 'next/head'
import React, { useEffect, useRef, useState } from 'react'
import { useMedia, useWindowSize } from 'react-use'
import styled from 'styled-components'
import { BrandStatus } from 'types/graphql'
import { assertExists } from 'utils/assert'
import { resizedImageUrl } from 'utils/imageUrl'
import { media, tabletMaxWidth } from 'utils/media'
import { useGrowthBook } from 'utils/useGrowthBook'
import { usePathParam } from 'utils/usePathParam'
import { Grid } from 'lib/Grid'
import { LoadingDots } from 'lib/LoadingDots'
import { Props as PageMetaProps } from 'lib/PageMeta'
import { MaxWidthContainer } from 'lib/Section'
import { ShowForMobile } from 'lib/ShowComponent'
import { Text } from 'lib/Text'
import { Header } from 'lib/layout/Header'
import { Page } from 'modules/Page'
import { Props as PageProps } from 'modules/Page'
import { useBrand } from 'modules/brands/queries/useBrand'
import { BrandImages } from 'modules/brands/show/BrandImages'
import { Facts } from 'modules/brands/show/Facts'
import { Info } from 'modules/brands/show/Info'
import { NotFound } from 'modules/brands/show/NotFound'
import { ProductDropLiftup } from 'modules/brands/show/ProductDropLiftup'
import { SpottedBy } from 'modules/brands/show/SpottedBy'
import { StickyTabNavigation } from 'modules/brands/show/StickyTabNavigation'
import { TransparencyBox } from 'modules/brands/show/TransparencyBox'
import { BrandContext } from 'modules/brands/show/context/CurrentBrandProvider'
import { useCreateBrandView } from 'modules/member/lists/queries/useCreateBrandView'
import { BrandsOwnRating, HARD_CODED_RATINGS } from 'modules/reviews/list/components/BrandsOwnRating'
import { Summary } from 'modules/reviews/list/components/Summary'
import { BrandReviewsProvider } from 'modules/reviews/list/context/BrandReviewsContext'
import { SortOrderOption } from 'modules/reviews/utils/sortOrder'
import { ProductCampaign } from './show/components/ProductCampaign'
import { JunaThingdropAd } from './show/experiments/JunaThingdropAd'

const TabContent = styled.div`
  margin-bottom: 36px;
  ${media.tabletDown} {
    padding: 0 var(--grid-gap-small);
  }
`

const Main = styled.main`
  grid-column: start / 8;
  order: 1;
  margin: 40px 0;
  ${media.tabletDown} {
    grid-column: start / end;
    margin: 0;
    order: 2;
  }
`

const Aside = styled.aside`
  grid-column: 9 / end;
  order: 2;
  margin: 40px 0;
  ${media.tabletDown} {
    grid-column: start / end;
    margin: 0;
    order: 1;
  }
`

const LoadingPadding = styled.div`
  margin: 48px;
`

const ShowForDesktop = styled.div`
  display: none;
  ${media.tabletUp} {
    display: block;
  }
`

const Banner = styled.div`
  align-items: center;
  background: var(--neutral-60);
  display: flex;
  justify-content: center;
  padding: 8px 16px;
`

export interface ReviewFilterParams {
  brandProductIds: string[]
  reviewId: string
  order: SortOrderOption
}

interface ExtraProps {
  tab?: string
  meta?: PageMetaProps
  params: ReviewFilterParams
}

const BrandPage = ({ children, meta = {}, tab, params }: PageProps & ExtraProps) => {
  const slug = usePathParam('slug')

  assertExists(slug, 'Brand slug is required to view BrandPage')

  const { data, loading } = useBrand(slug)
  const brand = data?.brand
  useCreateBrandView(brand?.id)

  const gb = useGrowthBook()

  // References
  const tabContentRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)

  // Sensors
  const { scrollY: viewportScrollY } = useScroll()
  const { height: windowHeight } = useWindowSize()

  // Scroll states
  const [scrollStart, setScrollStart] = useState(windowHeight)
  const [scrollEnd, setScrollEnd] = useState(windowHeight * 2)

  // Animation/transitions
  const opacity = useTransform(viewportScrollY, [scrollStart, scrollEnd], [0, 1])
  const top = useTransform(viewportScrollY, [scrollStart, scrollEnd], [-16, 0])
  const headerTop = useTransform(viewportScrollY, [scrollStart, scrollEnd], [0, -62])
  const scale = useTransform(viewportScrollY, [scrollStart, scrollEnd], [0.95, 1])
  const isTabletDown = useMedia(`(max-width: ${tabletMaxWidth}px)`, false)

  const handleTabChange = () => {
    if (!tabContentRef.current || !tabsRef.current || !isTabletDown) return

    const contentTop = tabContentRef.current.getBoundingClientRect().top
    const tabsHeight = tabsRef.current.getBoundingClientRect().height
    const scrollY = viewportScrollY.get()
    const padding = 24
    const topWithOffset = scrollY + contentTop - padding - tabsHeight

    if (scrollY > topWithOffset) {
      window.scrollTo({ top: topWithOffset })
    }
  }

  useEffect(() => {
    const rootRect = tabContentRef.current?.getBoundingClientRect()
    const tabRect = tabsRef.current?.getBoundingClientRect()
    if (rootRect && tabRect) {
      const scrollTop = viewportScrollY.get()
      const offsetStart = rootRect.top + scrollTop - tabRect.height
      const offsetEnd = offsetStart + tabRect.height

      setScrollStart(offsetStart)
      setScrollEnd(offsetEnd + 60)
    }
  }, [viewportScrollY, windowHeight])

  useEffect(() => {
    if (brand) {
      const payload = { brandId: brand.id, nonInteraction: 1 }
      window.analytics.track('Brand Viewed', payload)
    }
  }, [brand])

  if (loading) {
    return (
      <Page>
        <MaxWidthContainer>
          <LoadingPadding>
            <LoadingDots fill />
          </LoadingPadding>
        </MaxWidthContainer>
      </Page>
    )
  }

  if (!brand && !loading) {
    return (
      <Page>
        <MaxWidthContainer>
          <NotFound />
        </MaxWidthContainer>
      </Page>
    )
  }

  assertExists(brand)

  const headerStyle = { motionStyle: { top: isTabletDown ? headerTop : '0px' } }
  const brandRating = HARD_CODED_RATINGS[brand.slug]
  const rootCategory = brand.categories.find((c) => !!c.parentId)
  const showBrandsOwnRating = brandRating ? gb.isOn('brands-own-rating') : false

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              [
                {
                  '@context': 'https://schema.org/',
                  '@type': 'BreadcrumbList',
                  'itemListElement': [
                    {
                      '@type': 'ListItem',
                      'position': 1,
                      'name': 'Home',
                      'item': 'https://thingtesting.com/'
                    },
                    {
                      '@type': 'ListItem',
                      'position': 2,
                      'name': 'Brands',
                      'item': 'https://thingtesting.com/brands'
                    },
                    {
                      '@type': 'ListItem',
                      'position': 3,
                      'name': brand.title,
                      'item': `https://thingtesting.com/brands/${brand.slug}`
                    },
                    {
                      '@type': 'ListItem',
                      'position': 4,
                      'name': capitalize(tab),
                      'item': `https://thingtesting.com/brands/${brand.slug}/${tab}`
                    }
                  ]
                },
                rootCategory
                  ? {
                      '@context': 'https://schema.org/',
                      '@type': 'BreadcrumbList',
                      'itemListElement': [
                        {
                          '@type': 'ListItem',
                          'position': 1,
                          'name': 'Home',
                          'item': 'https://thingtesting.com/'
                        },
                        {
                          '@type': 'ListItem',
                          'position': 2,
                          'name': 'Categories',
                          'item': 'https://thingtesting.com/categories'
                        },
                        {
                          '@type': 'ListItem',
                          'position': 3,
                          'name': rootCategory.name,
                          'item': `https://thingtesting.com/categories/${rootCategory.slug}`
                        },
                        {
                          '@type': 'ListItem',
                          'position': 4,
                          'name': brand.title,
                          'item': `https://thingtesting.com/brands/${brand.slug}`
                        },
                        {
                          '@type': 'ListItem',
                          'position': 5,
                          'name': capitalize(tab),
                          'item': `https://thingtesting.com/brands/${brand.slug}/${tab}`
                        }
                      ]
                    }
                  : null
              ].filter(Boolean)
            )
          }}
        />
      </Head>

      <Page
        header={<Header {...headerStyle} />}
        meta={{
          description: brand.description
            ? `${brand.description} - Read trustworthy reviews of ${brand.title}`
            : `Read trustworthy reviews of ${brand.title}`,
          canonicalUrl: `https://thingtesting.com/brands/${brand.slug}${tab ? '/' + tab : ''}`,
          image: resizedImageUrl(brand.primaryImageFullPath, { width: 1080, height: 1080 }),
          index: brand.status === BrandStatus.Published,
          ...meta
        }}>
        <BrandContext.Provider value={{ brand }}>
          <BrandReviewsProvider params={params}>
            {brand.rip && (
              <Banner>
                <Text weight="semi" size="small" color="white">
                  {brand.title} has permanently closed down.
                </Text>
              </Banner>
            )}

            <Grid>
              <Main>
                <Info />
                <ShowForMobile>
                  {brand.productDrops.map((productDrop) => (
                    <ProductDropLiftup key={productDrop.id} drop={productDrop} />
                  ))}
                </ShowForMobile>
                <StickyTabNavigation onTabChange={handleTabChange} style={{ opacity, top, scale }} ref={tabsRef} />
                <TabContent id="tab-content" ref={tabContentRef}>
                  {children}
                </TabContent>
              </Main>
              <Aside>
                <BrandImages />
                <ShowForDesktop>
                  {brand.productDrops.map((productDrop) => (
                    <ProductDropLiftup key={productDrop.id} drop={productDrop} />
                  ))}
                  <Summary />
                  <ProductCampaign />
                  {showBrandsOwnRating && <BrandsOwnRating brandRating={brandRating} />}
                  <SpottedBy />
                  <Facts />
                  <JunaThingdropAd />
                  <TransparencyBox />
                </ShowForDesktop>
              </Aside>
            </Grid>
          </BrandReviewsProvider>
        </BrandContext.Provider>
      </Page>
    </>
  )
}

export default BrandPage
